/**
 * Stage local/desktop media (file://, /api/storage/local/) to S3 before server export.
 * Uses backend sync + canonical-key promotion (same as video snippets / hybrid storage).
 */
import api from '@/lib/api';
import { getBackendBaseUrl } from '@/services/config/env';

export function normalizeExportMediaRef(url) {
  const s = String(url || '').trim();
  if (!s) return s;
  const idx = s.indexOf('file://');
  if (idx > 0) return s.slice(idx);
  return s;
}

export function isLocalExportMediaRef(url) {
  const u = normalizeExportMediaRef(url);
  if (!u) return false;
  if (u.startsWith('file://')) return true;
  if (u.includes('/api/storage/local/')) return true;
  return false;
}

function filenameFromRef(ref) {
  const u = normalizeExportMediaRef(ref);
  const path = u.startsWith('file://') ? decodeURIComponent(u.replace(/^file:\/\//, '')) : u;
  const base = path.split('/').pop() || 'media.bin';
  return base.includes('.') ? base : `${base}.bin`;
}

function contentTypeFromRef(ref, fallback = 'application/octet-stream') {
  const low = filenameFromRef(ref).toLowerCase();
  if (low.endsWith('.mp4')) return 'video/mp4';
  if (low.endsWith('.webm')) return 'video/webm';
  if (low.endsWith('.mov')) return 'video/quicktime';
  if (low.endsWith('.mp3')) return 'audio/mpeg';
  if (low.endsWith('.wav')) return 'audio/wav';
  if (low.endsWith('.png')) return 'image/png';
  if (low.endsWith('.jpg') || low.endsWith('.jpeg')) return 'image/jpeg';
  if (low.endsWith('.webp')) return 'image/webp';
  return fallback;
}

/** Read bytes for export staging (file:// works in Electron; HTTP via backend). */
export async function fetchExportMediaBlob(url) {
  const ref = normalizeExportMediaRef(url);
  if (!ref) throw new Error('Missing media URL');
  if (ref.startsWith('blob:')) {
    throw new Error('Browser-only blob URLs cannot be uploaded for export');
  }
  if (ref.startsWith('file://')) {
    const res = await fetch(ref);
    if (!res.ok) throw new Error(`Could not read local file (${res.status})`);
    return res.blob();
  }
  const abs = ref.startsWith('http://') || ref.startsWith('https://')
    ? ref
    : `${getBackendBaseUrl()}${ref.startsWith('/') ? ref : `/${ref}`}`;
  const res = await fetch(abs);
  if (!res.ok) throw new Error(`Could not download media (${res.status})`);
  return res.blob();
}

/** Collect all media URLs from an export payload (video, voice, background music). */
export function collectExportMediaUrls(body) {
  const urlSet = new Set();
  for (const c of body.clips || []) {
    if (c.video_url) urlSet.add(c.video_url);
    if (c.image_url) urlSet.add(c.image_url);
  }
  for (const a of body.audio_clips || []) {
    if (a.audio_url) urlSet.add(a.audio_url);
  }
  for (const o of body.overlay_clips || []) {
    if (o.image_url) urlSet.add(o.image_url);
  }
  return [...urlSet];
}

async function stageViaBackend(urls, { narrationProjectId, storyId, onProgress }) {
  onProgress?.('Syncing local media to cloud…');
  try {
    await api.post('/storage/sync');
  } catch {
    /* sync may be skipped when S3 is not configured; promotion still attempts upload */
  }
  const { data } = await api.post('/media/stage-for-export', {
    urls,
    narration_project_id: narrationProjectId || undefined,
    story_id: storyId || undefined,
  });
  return data?.replacements || {};
}

/** Prefer stable public object URL (S3) for display and bookend export refs. */
export function resolveUploadedMediaObjectUrl(data) {
  if (!data || typeof data !== 'object') return '';
  const objectUrl = data.object_url || data.s3_url || data.view_url;
  if (objectUrl && String(objectUrl).startsWith('http')) return String(objectUrl).trim();
  const publicRef = String(data.public_ref || '').trim();
  if (publicRef.startsWith('http')) return publicRef;
  const apiUrl = String(data.url || '').trim();
  if (apiUrl.startsWith('http')) return apiUrl;
  if (apiUrl.startsWith('/api/media/')) return apiUrl;
  if (data.asset_id) return `/api/media/${data.asset_id}`;
  return apiUrl;
}

/**
 * Upload via server multipart (works without S3).
 */
export async function uploadFileViaServer(file, {
  narrationProjectId = null,
  storyId = null,
  onProgress = null,
} = {}) {
  const filename = file?.name || 'upload.bin';
  onProgress?.(`Uploading ${filename}…`);
  const form = new FormData();
  form.append('file', file);
  if (narrationProjectId) form.append('narration_project_id', narrationProjectId);
  if (storyId) form.append('story_id', storyId);
  const { data } = await api.post('/media/upload', form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return resolveUploadedMediaObjectUrl(data);
}

/**
 * Upload a browser File to cloud storage via presigned PUT (S3) or server fallback.
 */
export async function uploadFileViaPresignedUrl(
  file,
  { narrationProjectId = null, storyId = null, onProgress = null } = {},
) {
  const filename = file?.name || 'upload.bin';
  const contentType = file?.type || contentTypeFromRef(filename);

  try {
    onProgress?.(`Uploading ${filename}…`);
    const { data: presigned } = await api.post('/media/presigned-url', null, {
      params: { filename, content_type: contentType },
    });
    const uploadUrl = presigned.upload_url || presigned.presigned_url;
    const s3Key = presigned.s3_key;

    const putRes = await fetch(uploadUrl, {
      method: 'PUT',
      body: file,
      headers: { 'Content-Type': contentType },
    });
    if (!putRes.ok) {
      throw new Error(`Direct S3 upload failed (${putRes.status})`);
    }

    const registerBody = { s3_key: s3Key, content_type: contentType };
    if (narrationProjectId) registerBody.narration_project_id = narrationProjectId;
    if (storyId) registerBody.story_id = storyId;

    const { data: registered } = await api.post('/media/register-s3-upload', registerBody);
    return resolveUploadedMediaObjectUrl(registered);
  } catch (err) {
    const status = err?.response?.status;
    if (status === 401 || status === 403) throw err;
    return uploadFileViaServer(file, { narrationProjectId, storyId, onProgress });
  }
}

async function stageOneUrlPresigned(originalUrl, { narrationProjectId, storyId, onProgress }) {
  const ref = normalizeExportMediaRef(originalUrl);
  const filename = filenameFromRef(ref);
  const contentType = contentTypeFromRef(ref);
  onProgress?.(`Uploading ${filename}…`);

  const { data: presigned } = await api.post('/media/presigned-url', null, {
    params: { filename, content_type: contentType },
  });
  const uploadUrl = presigned.upload_url || presigned.presigned_url;
  const s3Key = presigned.s3_key;
  const blob = await fetchExportMediaBlob(ref);

  await fetch(uploadUrl, {
    method: 'PUT',
    body: blob,
    headers: { 'Content-Type': contentType },
  });

  const registerBody = { s3_key: s3Key, content_type: contentType };
  if (narrationProjectId) registerBody.narration_project_id = narrationProjectId;
  if (storyId) registerBody.story_id = storyId;

  const { data: registered } = await api.post('/media/register-s3-upload', registerBody);
  return registered.url || `/api/media/${registered.asset_id}`;
}

/**
 * Replace local refs in export payload with /api/media/{id} URLs.
 * @returns {Promise<object>} export body ready for POST export-video
 */
export async function stageExportPayloadForCloud(body, options = {}) {
  const {
    narrationProjectId = null,
    storyId = null,
    onProgress = null,
  } = options;

  const locals = collectExportMediaUrls(body).filter(isLocalExportMediaRef);
  if (!locals.length) return body;

  let map = new Map();
  try {
    const replacements = await stageViaBackend(locals, {
      narrationProjectId,
      storyId,
      onProgress,
    });
    map = new Map(Object.entries(replacements));
  } catch (err) {
    onProgress?.('Cloud staging fallback: direct upload…');
    for (const raw of locals) {
      const key = normalizeExportMediaRef(raw);
      if (map.has(key)) continue;
      const staged = await stageOneUrlPresigned(raw, { narrationProjectId, storyId, onProgress });
      map.set(key, staged);
      map.set(raw, staged);
    }
  }

  const remap = (u) => {
    if (!u) return u;
    return map.get(u) || map.get(normalizeExportMediaRef(u)) || u;
  };

  return {
    ...body,
    clips: (body.clips || []).map((c) => ({
      ...c,
      video_url: c.video_url ? remap(c.video_url) : c.video_url,
      image_url: c.image_url ? remap(c.image_url) : c.image_url,
    })),
    audio_clips: (body.audio_clips || []).map((a) => ({
      ...a,
      audio_url: a.audio_url ? remap(a.audio_url) : a.audio_url,
    })),
    overlay_clips: (body.overlay_clips || []).map((o) => ({
      ...o,
      image_url: o.image_url ? remap(o.image_url) : o.image_url,
    })),
  };
}

export function exportPayloadHasLocalMedia(body) {
  return collectExportMediaUrls(body).some(isLocalExportMediaRef);
}
